import { enderecoValido, RASCUNHO_VAZIO, type Destinatario, type Rascunho } from './rascunho';

/**
 * O que a URL de abertura pede ao app instalado (ADR 0041).
 *
 * Duas portas de entrada chegam aqui: o atalho "Novo e-mail" do manifesto
 * (`/?acao=novo`) e o `protocol_handlers`, que troca `%s` pelo link `mailto:`
 * inteiro e codificado (`/?mailto=mailto%3A...`). As duas so preenchem o
 * composer - nada aqui envia. O link `mailto:` vem de qualquer pagina da web,
 * entao so os campos que fazem sentido entram, e anexo nunca.
 */

export type AcaoDeAbertura = { tipo: 'novo' } | { tipo: 'mailto'; rascunho: Rascunho };

/** Um link `mailto:` gigante nao vira rascunho gigante. */
const LIMITE = 8_000;

export function acaoDaUrl(busca: string): AcaoDeAbertura | null {
  const parametros = new URLSearchParams(busca);
  const mailto = parametros.get('mailto');
  if (mailto !== null) {
    const rascunho = rascunhoDoMailto(mailto);
    return rascunho === null ? { tipo: 'novo' } : { tipo: 'mailto', rascunho };
  }
  if (parametros.get('acao') === 'novo') return { tipo: 'novo' };
  return null;
}

/** A busca sem os parametros que ja foram atendidos, para recarregar nao reabrir. */
export function semAcao(busca: string): string {
  const parametros = new URLSearchParams(busca);
  parametros.delete('mailto');
  parametros.delete('acao');
  const resto = parametros.toString();
  return resto === '' ? '' : `?${resto}`;
}

/**
 * RFC 6068. O `+` e literal num `mailto:` - o `URLSearchParams` o trocaria por
 * espaco e estragaria `maria+notas@empresa.com.br` - por isso a leitura a mao.
 */
export function rascunhoDoMailto(link: string): Rascunho | null {
  if (link.length > LIMITE) return null;
  const sem = link.replace(/^(web\+)?mailto:/i, '');
  if (sem === link) return null;

  const [caminho = '', consulta = ''] = sem.split('?', 2);
  const campos = new Map<string, string[]>();
  for (const par of consulta.split('&')) {
    if (par === '') continue;
    const [chave = '', valor = ''] = par.split('=', 2);
    const nome = decodificar(chave).toLowerCase();
    campos.set(nome, [...(campos.get(nome) ?? []), decodificar(valor)]);
  }

  const lista = (valores: string[]): Destinatario[] =>
    valores
      .flatMap((v) => v.split(','))
      .map((v) => v.trim())
      .filter(enderecoValido)
      .map((address) => ({ name: '', address }));

  const corpo = campos.get('body')?.[0] ?? '';

  return {
    ...RASCUNHO_VAZIO,
    to: lista([decodificar(caminho), ...(campos.get('to') ?? [])]),
    cc: lista(campos.get('cc') ?? []),
    bcc: lista(campos.get('bcc') ?? []),
    subject: campos.get('subject')?.[0] ?? '',
    html: corpo === '' ? '' : paragrafos(corpo),
  };
}

function decodificar(valor: string): string {
  try {
    return decodeURIComponent(valor);
  } catch {
    return valor;
  }
}

/** Texto puro para o HTML do editor, com tudo escapado: o corpo vem de fora. */
function paragrafos(texto: string): string {
  const escapar = (s: string) =>
    s
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  return texto
    .split(/\r?\n/)
    .map((linha) => `<p>${escapar(linha)}</p>`)
    .join('');
}
